import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { wholesale_forecast_by_account_plan_tree_path } from "@/lib/routes";
import { useMarket } from "@/lib/useMarket";
import FlatProductSearch, { type ProductResult } from "./FlatProductSearch";
import LeafProductList from "./LeafProductList";
import NodeDrilldown, { type PlanNode } from "./NodeDrilldown";

interface PlanTree {
  plan: { id: string; name: string } | null;
  nodes: PlanNode[];
}

interface Props {
  open: boolean;
  existingExternalIds: Set<string>;
  onAdd: (product: ProductResult) => void;
  onClose: () => void;
}

// "+ Add product" picker. Primary flow drills down the current TopDown
// plan's node tree (left) and lists the selected leaf's products (right),
// resolved server-side exactly like Bottom Up's eligible products. Orgs
// without a current plan fall back to the flat org-wide search.
export default function AddProductDialog({ open, existingExternalIds, onAdd, onClose }: Props) {
  const market = useMarket();
  const [tree, setTree] = useState<PlanTree | null>(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [selectedLeafId, setSelectedLeafId] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    setSelectedLeafId(null);
    setQuery("");
    setTreeLoading(true);
    fetch(wholesale_forecast_by_account_plan_tree_path(market.slug), { headers: { Accept: "application/json" } })
      .then((res) => res.json())
      .then((json: PlanTree) => setTree(json))
      .finally(() => setTreeLoading(false));
  }, [open, market.slug]);

  useEffect(() => {
    setQuery("");
  }, [selectedLeafId]);

  const hasTree = (tree?.nodes.length ?? 0) > 0;

  function handleOpenChange(next: boolean) {
    if (!next) onClose();
  }

  function handlePick(product: ProductResult) {
    onAdd(product);
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={hasTree ? "max-w-4xl" : undefined}>
        <DialogHeader>
          <DialogTitle>Add a product</DialogTitle>
          <DialogDescription>
            {hasTree
              ? `Drill down the ${tree?.plan?.name ?? "TopDown"} plan tree, then pick a product to start a new forecast group.`
              : "Search by product name, short name, product line short name, or product ID to start a new forecast group."}
          </DialogDescription>
        </DialogHeader>

        {treeLoading && (
          <div className="px-3 py-8 text-center text-xs text-muted-foreground">Loading plan…</div>
        )}

        {!treeLoading && hasTree && tree && (
          <div className="flex min-h-0 flex-col gap-3">
            <Input
              autoFocus
              placeholder={selectedLeafId ? "Filter products…" : "Search all products…"}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="grid h-96 grid-cols-[240px_minmax(0,1fr)] gap-3">
              <NodeDrilldown
                nodes={tree.nodes}
                selectedLeafId={selectedLeafId}
                onSelectLeaf={setSelectedLeafId}
              />
              <LeafProductList
                leafId={selectedLeafId}
                query={query}
                existingExternalIds={existingExternalIds}
                onPick={handlePick}
              />
            </div>
          </div>
        )}

        {!treeLoading && !hasTree && (
          <FlatProductSearch existingExternalIds={existingExternalIds} onPick={handlePick} />
        )}
      </DialogContent>
    </Dialog>
  );
}
